//@flow
import {
    FieldDataType,
    FieldFormatDataType
} from 'common/models/internal/EntitySettings';
import * as Immutable from 'immutable';
import CustomFieldsMultiSelectFilter from 'common/entity/components/CustomFieldsMultiSelectFilter';
import { FilterBuilder, FilterOperation } from 'common/models/internal/Filter';
import I18nHelper from 'common/helpers/I18nHelper';
import { memoizedImmutable } from 'common/utils/Memoizer';
import DataUtils from 'common/utils/DataUtils';
import type {
    EntitySettings,
    CustomField
} from 'common/models/internal/EntitySettings';
import type { List } from 'immutable';
import type { EntitySettingsModelType } from 'common/models/flow/EntitySettings';
import type { EntityColumnType } from 'common/entity/generic/MasterContainer';
import type UnitConversionHelper from 'common/helpers/UnitConversionHelper';
import type { MasterVehicleModel } from 'common/models/internal/MasterVehicleModel';
import { ResourceLimit } from 'common/models/internal/ResourceLimit';

const nsTranslate = I18nHelper.translate.bind(null, [
    'entityManagement',
    'translations'
]);

export const RESOURCE_PATH = ['limits', 'resourceLimits'];
export const VEHICLE_RESOURCE_PATH = [
    'vehicleModel',
    'limits',
    'resourceLimits'
];
export const RIDER_RESOURCE_PATH = ['riderPersona', 'limits', 'resourceLimits'];
export class CustomColumnHelpers {
    static addExtraProperties(
        columns: Array<Object>,
        extraProperties: Array<Object>
    ) {
        return columns
            .map(column => {
                const columnExtras = extraProperties.find(extraColumn => {
                    return (
                        column.fieldId === extraColumn.id ||
                        column.accessor === extraColumn.id ||
                        column.id === extraColumn.id
                    );
                });
                if (!columnExtras) {
                    return column;
                } else if (!columnExtras.visible) {
                    return undefined;
                }
                return {
                    ...column,
                    ...columnExtras
                };
            })
            .filter(Boolean);
    }

    static processColumnsTranslation({
        entitySettings,
        columns,
        unitConverter
    }: {
        entitySettings: EntitySettingsModelType,
        columns: Array<EntityColumnType>,
        unitConverter: UnitConversionHelper
    }): Array<EntityColumnType> {
        return columns.map(rawColumn => {
            const {
                headerTextId,
                preferredUnitText,
                translationProps: getTranslationProps
            } = rawColumn;
            let { headerText } = rawColumn;
            let column = rawColumn;
            if (headerTextId) {
                const translationProps = getTranslationProps
                    ? getTranslationProps({ t: nsTranslate, unitConverter })
                    : {};
                if (preferredUnitText) {
                    headerText = nsTranslate(headerTextId, {
                        unit: preferredUnitText,
                        ...translationProps
                    });
                } else {
                    headerText = nsTranslate(headerTextId, translationProps);
                }
            }
            column = {
                ...rawColumn,
                headerText,
                getHeaderProps: () => {
                    return {
                        t: nsTranslate,
                        entitySettings
                    };
                }
            };
            return column;
        });
    }

    static processColumns(
        entitySettings: ?EntitySettings,
        columns: Array<Object>,
        settingsName: string,
        extraProperties: Array<Object> = [],
        unitConverter: UnitConversionHelper,
        customResourceLimitsColumns: ?Array<EntityColumnType>
    ) {
        let modifiedColumns = this.processColumnsTranslation({
            columns,
            entitySettings,
            unitConverter
        });
        if (!entitySettings || !entitySettings[settingsName]) {
            return CustomColumnHelpers.addExtraProperties(
                modifiedColumns,
                extraProperties
            );
        }
        const { customFields } = entitySettings[settingsName];
        modifiedColumns = modifiedColumns.filter(rawColumn => {
            if (customFields) {
                const modelFieldColumn = customFields.find(
                    field =>
                        (field.key === rawColumn.fieldId ||
                            field.key === rawColumn.accessor) &&
                        field.metadata.isModelProperty
                );
                if (
                    modelFieldColumn &&
                    modelFieldColumn.metadata.visible === false
                ) {
                    return false;
                }
            }
            return true;
        });
        return CustomColumnHelpers.includeCustomColumns(
            entitySettings,
            modifiedColumns,
            settingsName,
            extraProperties,
            customResourceLimitsColumns
        );
    }

    static includeCustomColumns(
        entitySettings: ?EntitySettings,
        columns: Array<Object>,
        settingsName: string,
        extraProperties: Array<Object> = [],
        customResourceLimitsColumns: ?Array<EntityColumnType>
    ) {
        if (!entitySettings || !entitySettings[settingsName]) {
            return CustomColumnHelpers.addExtraProperties(
                modifiedColumns,
                extraProperties
            );
        }
        let { customFields } = entitySettings[settingsName];
        //Filter out custom resources from custom feild columns
        if (customResourceLimitsColumns) {
            customFields = customFields.filter(
                field => !field.metadata.isResource
            );
        }
        const modifiedColumns = [...columns];
        if (customFields) {
            (customFields: List<CustomField>)
                .filter(
                    field =>
                        field.metadata.visible &&
                        !field.metadata.isModelProperty
                )
                .forEach(customField => {
                    const { metadata, name, key } = customField;
                    const {
                        filterable,
                        sortable,
                        dataType,
                        format,
                        possibleValues
                    } = metadata;
                    const newColumn = {
                        headerText: name,
                        id: key,
                        visible: true,
                        property: `customProperties.${key}`,
                        accessor: data => {
                            const { customProperties } = data;
                            const value = customProperties.get(key);
                            if (dataType === FieldDataType.Boolean) {
                                switch (value) {
                                    case 'true': {
                                        return possibleValues.size === 2
                                            ? possibleValues.get(0)
                                            : nsTranslate('translations:yes');
                                    }
                                    case 'false': {
                                        return possibleValues.size === 2
                                            ? possibleValues.get(1)
                                            : nsTranslate('translations:no');
                                    }
                                }
                            }
                            return value;
                        },
                        dataType,
                        sortable
                    };
                    if (!filterable) {
                        // HACK: if filterable: true is passed to react-table, it shows filterable area even
                        // if filtering is disabled. Stupid bug! This is to bypass that.
                        newColumn.filterable = false;
                    }
                    if (
                        (dataType === FieldDataType.String &&
                            format === FieldFormatDataType.SingleChoice) ||
                        dataType === FieldDataType.Boolean
                    ) {
                        let options = possibleValues
                            .map(possibleValue => {
                                return {
                                    value: possibleValue,
                                    label: possibleValue
                                };
                            })
                            .toArray();
                        if (dataType === FieldDataType.Boolean) {
                            options = options.map((option, index) => {
                                return {
                                    value: index === 0 ? 'true' : 'false',
                                    label: option.label
                                };
                            });
                        }

                        newColumn.FilterComponent = CustomFieldsMultiSelectFilter;
                        newColumn.getFilterOptions = () => options;
                        newColumn.getCustomFilterInfo = ({
                            filteredColumn
                        }) => {
                            const {
                                value: { values }
                            } = filteredColumn;
                            return new FilterBuilder()
                                .withName(`customProperties.${key}`)
                                .withOperation(FilterOperation.Equals)
                                .withValues(values);
                        };
                    }
                    if (dataType) modifiedColumns.push(newColumn);
                });
        }
        return CustomColumnHelpers.addExtraProperties(
            modifiedColumns,
            extraProperties
        );
    }

    static makeCustomColumnProperties(
        entitySettings: ?EntitySettings,
        settingsName: string,
        onOptionClick: (string, string) => void,
        isOptionDisabled: () => boolean | boolean
    ) {
        if (!entitySettings || !entitySettings[settingsName]) {
            return [];
        }
        const { customFields } = entitySettings[settingsName];
        const properties = [];
        (customFields: List<CustomField>).forEach(customField => {
            const { metadata, key } = customField;
            if (!metadata.visible) {
                properties.push({
                    id: key,
                    visible: false
                });
                return;
            }
            const { dataType, format, possibleValues } = metadata;
            if (
                (dataType === FieldDataType.String &&
                    format === FieldFormatDataType.SingleChoice) ||
                dataType === FieldDataType.Boolean
            ) {
                let values = possibleValues;
                if (
                    dataType === FieldDataType.Boolean &&
                    possibleValues.length === 0
                ) {
                    // If no possibleValues for boolean, set to true and false
                    values = Immutable.List(['true', 'false']);
                }
                let menuValues;
                if (!values.size) {
                    menuValues = [
                        {
                            isEmptyState: true,
                            emptyStateText: nsTranslate(
                                'bulkEdit.menuEmptyStateText'
                            )
                        }
                    ];
                } else {
                    menuValues = [
                        {
                            isTitle: true,
                            label: nsTranslate('bulkEdit.setValueTo')
                        }
                    ].concat(
                        values
                            .map((possibleValue, index) => {
                                return {
                                    id: possibleValue,
                                    label: possibleValue,
                                    onClick: () => {
                                        let value = possibleValue;
                                        if (
                                            dataType === FieldDataType.Boolean
                                        ) {
                                            value =
                                                index === 0 ? 'true' : 'false';
                                        }
                                        onOptionClick(key, value);
                                    },
                                    isDisabled: isOptionDisabled
                                };
                            })
                            .toArray()
                    );
                }
                const menuOption = {
                    id: key,
                    visible: true,
                    menu: menuValues
                };
                properties.push(menuOption);
            }
        });
        return properties;
    }

    static getHiddenPropertySubFields({ key, hiddenModelProperties }) {
        // This regex checks if the field is like carton.weight or similar.
        // This will only work for one level nesting which is the case currently
        const regex = new RegExp(`${key}\\..*`, 'gi');
        const hiddenTempFields = hiddenModelProperties.filter(property =>
            regex.test(property)
        );
        const hiddenTempSubFields = [];
        if (hiddenTempFields && hiddenTempFields.length > 0) {
            hiddenTempFields.forEach(field => {
                hiddenTempSubFields.push(field.substr(field.indexOf('.') + 1));
            });
        }
        return hiddenTempSubFields;
    }

    @memoizedImmutable()
    static getOptionsByKeyName(entitySettings, settingsName, key) {
        if (!entitySettings) {
            return [];
        }
        const possibleValues = entitySettings.getPossibleValuesForModelField(
            settingsName,
            key
        );
        const options = [];
        if (possibleValues) {
            //forEach because possibleValues is List and the result needs to be JS array
            possibleValues.forEach(value => {
                options.push(value);
            });
        }
        return options;
    }

    static getCustomResources(
        entitySettings: EntitySettings,
        settingsName: string
    ): List<CustomField> {
        const { customFields } = entitySettings.get(settingsName);
        return customFields.filter(field => field.metadata.isResource);
    }

    static getCustomFields(
        entitySettings: EntitySettings,
        settingsName: string,
        skipResources?: boolean
    ): List<CustomField> {
        const { customFields } = entitySettings.get(settingsName);
        if (skipResources) {
            return customFields.filter(field => !field.metadata.isResource);
        }
        return customFields;
    }

    static getCustomResource(
        entity: MasterVehicleModel,
        resourceLimitsPath: Array<string>,
        resourceName: string
    ): ?ResourceLimit {
        const resourceLimits = entity.getIn(resourceLimitsPath);
        return resourceLimits.find(resource => resource.name === resourceName);
    }

    static getCustomResourceColumns(
        entitySettings: EntitySettings,
        settingsName: string,
        resourceLimitsPath: Array<string>
    ): ?Array<EntityColumnType> {
        const customResources = CustomColumnHelpers.getCustomResources(
            entitySettings,
            settingsName
        );
        const resourceColumns = [];
        customResources.forEach(customField => {
            const { name, key } = customField;
            const minColumn = {
                headerText: `Min. ${name}`,
                id: `${key}.lowerThresh`,
                Cell: props => {
                    const { original: entity } = props;
                    const customResource = CustomColumnHelpers.getCustomResource(
                        entity,
                        resourceLimitsPath,
                        key
                    );
                    const value = customResource?.lowerThresh;
                    return DataUtils.isEmpty(value) ? '' : value;
                },
                sortable: false,
                filterable: false
            };
            const maxColumn = {
                headerText: `Max. ${name}`,
                id: `${key}.higherThresh`,
                Cell: props => {
                    const { original: entity } = props;
                    const customResource = CustomColumnHelpers.getCustomResource(
                        entity,
                        resourceLimitsPath,
                        key
                    );
                    const value = customResource?.higherThresh;
                    return DataUtils.isEmpty(value) ? '' : value;
                },
                sortable: false,
                filterable: false
            };
            resourceColumns.push(...[minColumn, maxColumn]);
        });
        return resourceColumns;
    }
}

export default CustomColumnHelpers;
